import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { useMediaQuery, useTheme } from "@mui/system";

const PIPE_X = 24;

interface PipeDiagramProps {
  distance: number[];
  pipeLengthValue: number;
  view: "detected" | "extracted" | "combined";
  selectedIndex?: number | null;
  activeIndex?: number | null;
  mainCodes: string[];
  mainCodeColors: { mainCode: string; color: string }[];
  /** Fill for the pipe and its end circles. Defaults to the machine-output blue. */
  pipeColor?: string;
}

const PipeDiagram: React.FC<PipeDiagramProps> = ({ distance, pipeLengthValue, mainCodes, mainCodeColors, selectedIndex, activeIndex, view, pipeColor = "#88aacc" }) => {
  const theme = useTheme();
  const isIpad = useMediaQuery(theme.breakpoints.down("lg"));
  const ref = useRef<SVGSVGElement | null>(null);
  const pipeElements = distance.map((d, i) => ({
    distance: d,
    mainCode: mainCodes[i],
  }));

  const sortedData = [...pipeElements].sort((a, b) => a.distance - b.distance);
  const pipeLength = pipeLengthValue ?? Math.max(...distance, 50);
  const pipeTopPadding = 20;
  const fallbackPipeHeight = Math.max(distance.length * 64, 300);

  const [measuredHeight, setMeasuredHeight] = useState(0);
  useEffect(() => {
    let frame = 0;
    const findInspectionGrid = (): HTMLElement | null => {
      const svg = ref.current;
      if (!svg) return null;
      let container: HTMLElement | null = svg.parentElement;
      while (container) {
        const grid = container.querySelector<HTMLElement>(".inspection-grid");
        if (grid && !grid.contains(svg)) return grid;
        container = container.parentElement;
      }
      return document.querySelector<HTMLElement>(".inspection-grid");
    };
    const update = () => {
      const grid = findInspectionGrid();
      if (!grid) return;
      const h = grid.getBoundingClientRect().height;
      if (h > 0) setMeasuredHeight((prev) => (Math.abs(prev - h) > 0.5 ? h : prev));
    };
    const bump = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };
    update();
    let observer: ResizeObserver | null = null;
    const grid = findInspectionGrid();
    const target = grid?.querySelector<HTMLElement>("table") || grid;
    if (target && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(bump);
      observer.observe(target);
    }
    return () => {
      window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
    };
  }, [view, distance.length]);

  const svgWidth = isIpad ? PIPE_X + 60 : PIPE_X + 110;

  const pipeHeight = Math.max((measuredHeight || fallbackPipeHeight) - pipeTopPadding * 2, 300);
  const totalHeight = pipeHeight + pipeTopPadding * 2;

  const yScale = d3
  .scaleLinear()
  .domain([0, pipeLength])
  .range([pipeTopPadding, pipeTopPadding + pipeHeight]);

  useEffect(() => {
    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();
    const circleRadius = 15;
    const minY = pipeTopPadding + circleRadius;
    const maxY = pipeTopPadding + pipeHeight - circleRadius;

    const getRowCenterY = (index: number): number => {
      const row = document.querySelector(`.row-${index}`);
      if (!row) return 0;

      const rect = row.getBoundingClientRect();
      const svgRect = ref.current?.getBoundingClientRect();
      if (!svgRect) return 0;

      return rect.top - svgRect.top + rect.height / 2;
  };

    // Pipe hugs the left edge: the end circles (r 15) and the red playhead tick (14px left of
    // the pipe) still fit, and every pixel saved here goes to the inspection grid beside it.
    const pipeX = PIPE_X;
    const pipeWidth = 20;
    const markerSize = isIpad ? 4 : 6;
    const refX = isIpad ? 7 : 10;
    svg
      .append("rect")
      .attr("x", pipeX - pipeWidth / 2)
      .attr("y", pipeTopPadding)
      .attr("width", pipeWidth)
      .attr("height", pipeHeight)
      .attr("fill", pipeColor)
      .attr("rx", 10);

    svg
      .append("circle")
      .attr("cx", pipeX)
      .attr("cy", pipeTopPadding)
      .attr("r", 15)
      .attr("fill", pipeColor);

    svg
      .append("circle")
      .attr("cx", pipeX)
      .attr("cy", pipeTopPadding + pipeHeight)
      .attr("r", 15)
      .attr("fill", pipeColor);

    svg
      .append("defs")
      .append("marker")
      .attr("id", "arrow")
      .attr("viewBox", "0 0 10 10")
      .attr("refX", refX)
      .attr("refY", 5)
      .attr("markerWidth", markerSize)
      .attr("markerHeight", markerSize)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M 0 0 L 10 5 L 0 10 z")
      .attr("fill", "context-stroke");

      const start = selectedIndex != null && selectedIndex < distance.length ? distance[selectedIndex] : null;
      const end = selectedIndex != null && selectedIndex + 1 < distance.length ? distance[selectedIndex + 1] : pipeLength;

      if (start !== null && end !== null) {
        let yStart = yScale(start); 
        let yEnd = yScale(end);

        yStart = Math.max(minY, Math.min(maxY, yStart));
        yEnd = Math.max(minY, Math.min(maxY, yEnd));

        if (yStart === yEnd) {
          svg
            .append("line")
            .attr("x1", pipeX - pipeWidth / 2)
            .attr("y1", yStart)
            .attr("x2", pipeX + pipeWidth / 2)
            .attr("y2", yEnd)
            .attr("stroke", "gray")
            .attr("stroke-width", 3);
        }else{
          const yTop = Math.min(yStart, yEnd);
          const yBottom = Math.max(yStart, yEnd);
          svg
            .append("rect")
            .attr("x", pipeX - pipeWidth / 2)
            .attr("y", yTop)
            .attr("width", pipeWidth)
            .attr("height", yBottom - yTop)
            .attr("fill", "gray");
        } 
    }

    // Red "you are here" line tracking the currently-playing detection.
    if (activeIndex != null && activeIndex >= 0 && activeIndex < distance.length) {
      const yActive = Math.max(minY, Math.min(maxY, yScale(distance[activeIndex])));
      svg
        .append("line")
        .attr("x1", pipeX - pipeWidth / 2 - 14)
        .attr("y1", yActive)
        .attr("x2", pipeX + pipeWidth / 2 + 4)
        .attr("y2", yActive)
        .attr("stroke", "#e53935")
        .attr("stroke-width", 2);
    }

    const arrowsTimer = window.setTimeout(() => {
      sortedData.forEach((item, index) => {
        let startY = yScale(item.distance);

        const circleRadius = 15;
        const minY = pipeTopPadding + circleRadius;
        const maxY = pipeTopPadding + pipeHeight - circleRadius;
        startY = Math.max(minY, Math.min(maxY, startY));

        const rowIndex = index;
        const endY = Math.round(getRowCenterY(rowIndex));
        const startX = pipeX + pipeWidth / 2;
        const endX = svgWidth;
        const bendX = pipeX + 40;

        const color = mainCodeColors.find(c => c.mainCode === item.mainCode)?.color || "gray";

        svg
          .append("line")
          .attr("x1", Math.round(startX))
          .attr("y1", Math.round(startY))
          .attr("x2", Math.round(bendX))
          .attr("y2", Math.round(startY))
          .attr("stroke", color)
          .attr("stroke-width", 1.5);

        svg
          .append("line")
          .attr("x1", Math.round(bendX))
          .attr("y1", Math.round(startY))
          .attr("x2", Math.round(endX))
          .attr("y2", Math.round(endY))
          .attr("stroke", color)
          .attr("stroke-width", 1.5)
          .attr("marker-end", "url(#arrow)");
      });
    }, 50);
    return () => {
      window.clearTimeout(arrowsTimer);
    };
  }, [distance, selectedIndex, activeIndex, pipeLengthValue, mainCodes, mainCodeColors, isIpad, measuredHeight, view, pipeColor]);

  return <svg ref={ref} width={svgWidth} height={totalHeight} style={{ overflow: "visible" }} />;
};

export default PipeDiagram;